/**
 * How the standalone transcript viewer (`/view/transcript/:serviceId`) gets
 * its text — card 422, 25. 9. 2026.
 *
 * A live HTTP-writer transcript used to be read by polling
 * `GET /api/writer/:id/text?rev=N`: a new request 500 ms after every "no
 * change" and immediately after new text, never pausing in a background tab.
 * Production measured 2.6 requests/s for one viewer (36,551 requests in 4 h),
 * each one an auth DB query plus two whole-transcript Redis reads.
 *
 * The server already pushes the same writer's text to WebSocket subscribers of
 * the serviceId as small tail deltas (`http-writer-broadcast.ts`), under the
 * same space/room ACL as the HTTP read (`authorizeSubscribe`). So a cloud
 * viewer now always subscribes over the socket, and HTTP polling survives only
 * as a slow fallback for a live transcript whose socket cannot connect.
 * Local (LAN) mode is unchanged: it has no socket to use.
 */
/** Local (LAN) mode poll cadence — unchanged by card 422. */
export const LOCAL_SOURCE_POLL_MS = 500;
/** Fallback HTTP poll cadence when the socket cannot connect. */
export const LIVE_HTTP_FALLBACK_POLL_MS = 5000;
/**
 * How long the socket may stay unconnected before the HTTP fallback starts.
 * Covers the first connect plus the first two reconnect backoffs
 * (1 s + 2 s in `PolygrafConnection`), so a blip never starts a poll.
 */
export const LIVE_SOCKET_FALLBACK_AFTER_MS = 8000;
/**
 * While in the HTTP fallback, how often the viewer restarts the socket's
 * reconnect cycle. `PolygrafConnection` gives up after 5 attempts (~31 s of
 * backoff); without a restart a visible tab would stay on the 5 s poll for
 * good. Longer than one full backoff cycle, so a restart never cuts one short.
 */
export const LIVE_SOCKET_RETRY_MS = 45_000;
/** Wait after a failed poll, before the next one (local mode, and the floor). */
export const POLL_ERROR_BACKOFF_MS = 2000;
/** Pick the transport(s) the standalone transcript viewer should run. */
export function planTranscriptTransport(input) {
    if (input.isLocalMode) {
        return {
            httpPoll: {
                intervalMs: LOCAL_SOURCE_POLL_MS,
                pauseWhileHidden: false,
                paceAfterUpdates: false,
            },
            socketRetryMs: null,
        };
    }
    const socketCannotConnect = !input.socketConnected &&
        input.socketDownForMs !== null &&
        input.socketDownForMs >= LIVE_SOCKET_FALLBACK_AFTER_MS;
    // Only a live HTTP writer has an HTTP endpoint to fall back to; a WS-writer
    // document exists only on the socket.
    const fallback = input.isLive && !input.socketAccessDenied && socketCannotConnect;
    return {
        httpPoll: fallback
            ? {
                intervalMs: LIVE_HTTP_FALLBACK_POLL_MS,
                pauseWhileHidden: true,
                paceAfterUpdates: true,
            }
            : null,
        socketRetryMs: fallback ? LIVE_SOCKET_RETRY_MS : null,
    };
}
/**
 * The document the standalone viewer renders. Across a transport change it
 * keeps what is on screen until the new transport has something at least as
 * current: the fallback's first poll, or the reconnected socket's snapshot.
 *
 * MU N3c (7. 10. 2026): leaving the HTTP fallback switched straight to the
 * socket's copy from before the outage — for a few frames a much shorter
 * transcript, which clamped the scroll position before the snapshot restored
 * the text. Readers saw the whole document vanish, reload and jump.
 */
export function chooseShownDocument(input) {
    if (input.isLocalMode)
        return input.polled;
    if (input.isHttpFallback)
        return input.polled ?? input.shown ?? input.socket;
    if (!input.socketCurrent)
        return input.shown ?? input.socket;
    return input.socket;
}
/**
 * Whether a {@link TranscriptTransportPlan.socketRetryMs} tick should restart
 * the socket's reconnect cycle. A hidden page is left alone: the socket hook
 * already probes / reconnects on the hidden→visible transition.
 */
export function shouldRetrySocket(input) {
    return !input.pageHidden && !input.socketConnected;
}
/**
 * How long a poll loop waits after a failed request. A slowed poll (the live
 * fallback: paused while hidden or paced after updates) never retries faster
 * than its own cadence, so a failing endpoint does not raise the request rate
 * above the healthy one.
 */
export function pollErrorBackoffMs(poll) {
    const slowed = poll.pauseWhileHidden || poll.paceAfterUpdates;
    return slowed ? Math.max(POLL_ERROR_BACKOFF_MS, poll.intervalMs) : POLL_ERROR_BACKOFF_MS;
}
/**
 * Resolve once `doc` is visible, or when `signal` aborts. Resolves at once
 * when there is no document (SSR, workers) or it is already visible.
 */
export function waitUntilVisible(doc, signal) {
    if (!doc || !doc.hidden || signal?.aborted)
        return Promise.resolve();
    return new Promise((resolve) => {
        const finish = () => {
            doc.removeEventListener('visibilitychange', onChange);
            signal?.removeEventListener('abort', finish);
            resolve();
        };
        const onChange = () => {
            if (!doc.hidden)
                finish();
        };
        doc.addEventListener('visibilitychange', onChange);
        signal?.addEventListener('abort', finish);
    });
}
//# sourceMappingURL=transcript-transport.js.map